import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  LightbulbIcon,
  Home,
  DollarSign,
  Info,
  Ruler,
  CalendarIcon,
  FileText,
  ChevronDown,
  ChevronUp,
  ClipboardEdit,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { Input as InputField } from '@/components/ui/input';

// We're keeping this interface the same to maintain backward compatibility
interface PropertyRecord {
  address: string;
  subdivision: string;
  county: string;
  parcelAccountNumber: string;
  apn: string;
  assessmentYear: number;
  marketValue: number;
  assessedValue: number;
  assessedLandValue: number;
  assessedImprovementValue: number;
  marketLandValue: number;
  marketImprovementValue: number;
  taxAmount: string;
  livingSquareFeet: number;
  buildingSquareFeet: number;
  lotAcres: number;
  lotSquareFeet: number;
  bedrooms: number;
  bathrooms: number;
  partialBathrooms: number;
  stories: number;
  roomsCount: number;
  parkingSpaces: number;
  garageType: string;
  garageSquareFeet: number;
  basementType: string;
  basementSquareFeet: number;
  basementSquareFeetFinished: number;
  construction: string;
  roofMaterial: string;
  interiorStructure: string;
  airConditioningType: string;
  heatingType: string;
  heatingFuelType: string;
  fireplace: boolean;
  plumbingFixturesCount: number;
  hoa: boolean;
  pool: boolean;
  deck: boolean;
  patio: boolean;
  porchType: string | null;
  porchArea: number | null;
  rvParking: boolean;
  attic: boolean;
  breezeway: boolean;
  carport: boolean;
  safetyFireSprinklers: boolean;
  legalDescription: string;
  lotNumber: string;
  propertyUse: string;
  propertyUseCode: number;
  unitsCount: number;
  buildingsCount: number;
  taxExemptionHomeownerFlag: boolean;
  taxDelinquentYear: number | null;
  latitude: number;
  longitude: number;
  censusBlock: string;
  censusBlockGroup: string;
  censusTract: string;
  floodZoneDescription: string;
  lastSaleDate: string;
  pricePerSquareFoot: number;
  estimatedValue: number | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mls: Record<string, any>;
  lastSalePrice?: string;
}

interface PropertyDisplayProps {
  propertyData: PropertyRecord;
}

// Format currency values
const formatCurrency = (value: number | string) => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(numValue);
};

// Format areas with proper units
const formatArea = (sqft: number | null | undefined, unit = 'sq ft') => {
  if (sqft === null || sqft === undefined || sqft === 0) return null;
  return `${new Intl.NumberFormat('en-US').format(sqft)} ${unit}`;
};

const PropertyDisplay: React.FC<PropertyDisplayProps> = ({ propertyData }) => {
  const [openSections, setOpenSections] = React.useState<
    Record<string, boolean>
  >({
    size: true,
    construction: false,
    amenities: false,
    legal: false,
    geo: false,
    sales: false,
  });
  const [officialValuation, setOfficialValuation] = useState<string>('');
  const [fairMarketValue, setFairMarketValue] = useState<string>('');

  console.log(propertyData);

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };
  const handleValuationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove non-numeric characters except dots
    const value = e.target.value.replace(/[^0-9]/g, '');
    setOfficialValuation(value);
  };
  const handleFairMarketValueChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    // Remove non-numeric characters except dots
    const value = e.target.value.replace(/[^0-9]/g, '');
    setFairMarketValue(value);
  };

  // Format the valuation as currency for display
  const formattedValuation = officialValuation
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(Number(officialValuation))
    : '';

  // Format the fair market value as currency for display
  const formattedFairMarketValue = fairMarketValue
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(Number(fairMarketValue))
    : '';

  // Calculate total bathrooms
  const totalBathrooms =
    (propertyData.bathrooms || 0) + (propertyData.partialBathrooms || 0) * 0.5;

  return (
    <div className="w-full space-y-6 animate-fade-in font-sans">
      {/* Property Summary Header */}
      <Card className="border-tax-borderGray bg-tax-lightGray">
        <div className="p-5">
          <div className="flex flex-col md:flex-row md:justify-between">
            <div>
              <h3 className="text-xl font-bold text-tax-deepBlue">
                {propertyData.address}
              </h3>
              {propertyData.subdivision && (
                <p className="text-tax-neutralGray">
                  {propertyData.subdivision}
                </p>
              )}
            </div>
            <div className="mt-2 md:mt-0 md:text-right">
              <div className="inline-flex items-center bg-tax-deepBlue text-white px-3 py-1 rounded-md">
                <span className="font-semibold">{propertyData.county}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            <div className="flex flex-col">
              <span className="text-sm text-tax-neutralGray">
                Parcel Account #
              </span>
              <span className="font-semibold">
                {propertyData.parcelAccountNumber}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-sm text-tax-neutralGray">APN</span>
              <span className="font-semibold">{propertyData.apn}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-sm text-tax-neutralGray">
                Assessment Year
              </span>
              <span className="font-semibold">
                {propertyData.assessmentYear}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Alert Tip */}
      <Alert className="bg-tax-softWhite border-tax-brightBlue border-l-4">
        <div className="flex gap-3">
          <LightbulbIcon className="h-5 w-5 text-tax-brightBlue" />
          <AlertDescription>
            <span className="font-medium">Important Tip:</span> If any of this
            information is incorrect and you believe that by correcting it your
            property may have a lower value, be sure and include this in your
            appeal letter.
          </AlertDescription>
        </div>
      </Alert>

      {/* Valuation Information */}
      <Card className="border-tax-borderGray">
        <div className="p-5">
          <div className="flex items-center mb-3">
            <DollarSign className="h-5 w-5 mr-2 text-tax-brightBlue" />
            <h3 className="text-lg font-semibold text-tax-deepBlue">
              Assessor's Current Valuation (Too high? Scroll down to protest
              it.)
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-tax-lightGray rounded-lg p-3">
              <div className="text-sm text-tax-neutralGray">Market Value</div>
              <div className="text-xl font-bold text-tax-deepBlue">
                {propertyData.marketValue === 0
                  ? 'Not reported'
                  : formatCurrency(propertyData.marketValue)}
              </div>
            </div>
            <div className="bg-tax-lightGray rounded-lg p-3">
              <div className="text-sm text-tax-neutralGray">Assessed Value</div>
              <div className="text-xl font-bold text-tax-deepBlue">
                {formatCurrency(propertyData.assessedValue)}
              </div>
            </div>
            <div className="bg-tax-lightGray rounded-lg p-3">
              <div className="text-sm text-tax-neutralGray">
                Annual Property Tax
              </div>
              <div className="text-xl font-bold text-tax-deepBlue">
                {formatCurrency(propertyData.taxAmount)}
              </div>
            </div>
          </div>

          {/* Official Valuation Input */}
          {/* <div className="mt-4 bg-tax-lightGray rounded-lg p-4 border-l-4 border-tax-brightBlue">
            <div className="flex items-center mb-2">
              <ClipboardEdit className="h-5 w-5 mr-2 text-tax-brightBlue" />
              <h4 className="text-base font-bold text-tax-brightBlue">
                The official assessors valuation of our property:
              </h4>
            </div>
            <div className="flex items-center">
              <div className="relative flex-grow">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  $
                </span>
                <InputField
                  type="text"
                  placeholder="0"
                  value={officialValuation}
                  onChange={(e) =>
                    setOfficialValuation(e.target.value.replace(/[^0-9]/g, ''))
                  }
                  className="pl-8 bg-white"
                />
              </div>
              {officialValuation && (
                <div className="ml-3 text-tax-deepBlue font-semibold">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0,
                  }).format(Number(officialValuation))}
                </div>
              )}
            </div>
          </div> */}

          {/* New Fair Market Value Input */}
          {/* <div className="mt-4 bg-tax-lightGray rounded-lg p-4 border-l-4 border-tax-brightBlue">
            <div className="flex items-center mb-2">
              <ClipboardEdit className="h-5 w-5 mr-2 text-tax-brightBlue" />
              <h4 className="text-base font-bold text-tax-brightBlue">
                Your opinion of the fair market value of your property as of the
                assessment date:
              </h4>
            </div>
            <div className="flex items-center">
              <div className="relative flex-grow">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                  $
                </span>
                <InputField
                  type="text"
                  placeholder="0"
                  value={fairMarketValue}
                  onChange={(e) =>
                    setFairMarketValue(e.target.value.replace(/[^0-9]/g, ''))
                  }
                  className="pl-8 bg-white"
                />
              </div>
              {fairMarketValue && (
                <div className="ml-3 text-tax-deepBlue font-semibold">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0,
                  }).format(Number(fairMarketValue))}
                </div>
              )}
            </div>
          </div> */}

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium mb-1">
                Market Value Breakdown
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-tax-neutralGray">Land</span>
                <span>{formatCurrency(propertyData.marketLandValue)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-tax-neutralGray">Improvements</span>
                <span>
                  {formatCurrency(propertyData.marketImprovementValue)}
                </span>
              </div>
            </div>
            <div>
              <div className="text-sm font-medium mb-1">
                Assessed Value Breakdown
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-tax-neutralGray">Land</span>
                <span>{formatCurrency(propertyData.assessedLandValue)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-tax-neutralGray">Improvements</span>
                <span>
                  {formatCurrency(propertyData.assessedImprovementValue)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Property Size & Layout */}
      <Collapsible
        open={openSections.size}
        onOpenChange={() =>
          setOpenSections((prev) => ({ ...prev, size: !prev.size }))
        }
        className="border rounded-lg border-tax-borderGray"
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left">
          <div className="flex items-center">
            <Ruler className="h-5 w-5 mr-2 text-tax-brightBlue" />
            <h3 className="text-lg font-semibold text-tax-deepBlue">
              Property Size & Layout
            </h3>
          </div>
          {openSections.size ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Living Area</span>
                <span className="font-medium">
                  {formatArea(propertyData.livingSquareFeet)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Building Size</span>
                <span className="font-medium">
                  {formatArea(propertyData.buildingSquareFeet)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Lot Size</span>
                <span className="font-medium">
                  {formatArea(propertyData.lotSquareFeet)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Lot Acres</span>
                <span className="font-medium">
                  {propertyData.lotAcres || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Bedrooms</span>
                <span className="font-medium">
                  {propertyData.bedrooms || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Bathrooms</span>
                <span className="font-medium">
                  {propertyData.bathrooms
                    ? `${totalBathrooms} (${propertyData.bathrooms} full, ${propertyData.partialBathrooms} half)`
                    : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Stories</span>
                <span className="font-medium">
                  {propertyData.stories || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Room Count</span>
                <span className="font-medium">
                  {propertyData.roomsCount || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Parking Spaces</span>
                <span className="font-medium">
                  {propertyData.parkingSpaces || 'N/A'}
                </span>
              </div>
              {propertyData.garageType && (
                <div className="flex justify-between">
                  <span className="text-tax-neutralGray">Garage</span>
                  <span className="font-medium">
                    {propertyData.garageType} (
                    {formatArea(propertyData.garageSquareFeet)})
                  </span>
                </div>
              )}
              {propertyData.basementType && (
                <div className="flex justify-between">
                  <span className="text-tax-neutralGray">Basement</span>
                  <span className="font-medium">
                    {propertyData.basementType}{' '}
                    {formatArea(propertyData.basementSquareFeetFinished)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* MLS Property Size & Layout */}
      {/* {propertyData.mls && (
        <Collapsible
          open={openSections.size}
          onOpenChange={() =>
            setOpenSections((prev) => ({ ...prev, size: !prev.size }))
          }
          className="border rounded-lg border-tax-borderGray"
        >
          <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left">
            <div className="flex items-center">
              <Ruler className="h-5 w-5 mr-2 text-tax-brightBlue" />
              <h3 className="text-lg font-semibold text-tax-deepBlue">
                Property Size & Layout from MLS
              </h3>
            </div>
            {openSections.size ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="px-4 pb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                <div className="flex justify-between">
                  <span className="text-tax-neutralGray">Living Area</span>
                  <span className="font-medium">
                    {formatArea(propertyData.mls?.LivingArea)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-tax-neutralGray">Building Size</span>
                  <span className="font-medium">
                    {propertyData.mls?.BuildingAreaTotal}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-tax-neutralGray">Lot Size</span>
                  <span className="font-medium">
                    {propertyData.mls?.LotSizeArea
                      ? `${propertyData.mls?.LotSizeArea} ${propertyData.mls?.LotSizeUnits}`
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-tax-neutralGray">Lot Acres</span>
                  <span className="font-medium">
                    {propertyData.mls?.LotSizeAcres
                      ? `${propertyData.mls?.LotSizeAcres} ${propertyData.mls?.LotSizeUnits}`
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-tax-neutralGray">Bedrooms</span>
                  <span className="font-medium">
                    {propertyData.mls?.BedroomsTotal || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-tax-neutralGray">Bathrooms</span>
                  <span className="font-medium">
                    {propertyData.mls?.BathroomsTotalInteger
                      ? `${propertyData.mls?.BathroomsTotalInteger} (${propertyData.mls?.BathroomsFull} full, ${propertyData.mls?.BathroomsHalf} half, ${propertyData.mls?.BathroomsThreeQuarter} quarter)`
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-tax-neutralGray">Stories</span>
                  <span className="font-medium">
                    {propertyData.stories || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-tax-neutralGray">Room Count</span>
                  <span className="font-medium">
                    {propertyData.mls?.RoomsTotal || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-tax-neutralGray">Parking Spaces</span>
                  <span className="font-medium">
                    {propertyData.mls?.ParkingTotal || 'N/A'}
                  </span>
                </div>
                {propertyData.garageType && (
                  <div className="flex justify-between">
                    <span className="text-tax-neutralGray">Garage</span>
                    <span className="font-medium">
                      {propertyData.mls?.GarageSpaces}
                    </span>
                  </div>
                )}
                {propertyData.basementType && (
                  <div className="flex justify-between">
                    <span className="text-tax-neutralGray">Basement</span>
                    <span className="font-medium">
                      {propertyData.mls?.Basement?.length
                        ? propertyData.mls?.Basement.join(' ')
                        : 'N/A'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      )} */}

      {/* Construction & Systems */}
      {/* <Collapsible
        open={openSections.construction}
        onOpenChange={() =>
          setOpenSections((prev) => ({
            ...prev,
            construction: !prev.construction,
          }))
        }
        className="border rounded-lg border-tax-borderGray"
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left">
          <div className="flex items-center">
            <Home className="h-5 w-5 mr-2 text-tax-brightBlue" />
            <h3 className="text-lg font-semibold text-tax-deepBlue">
              Construction & Systems
            </h3>
          </div>
          {openSections.construction ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Construction</span>
                <span className="font-medium">
                  {propertyData.construction || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Roof Material</span>
                <span className="font-medium">
                  {propertyData.roofMaterial || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray text-left min-w-[95px] shrink-0">
                  Interior Walls
                </span>
                <span className="font-medium text-right break-words whitespace-normal flex-1">
                  {propertyData.interiorStructure?.split('/').join(' / ') ||
                    'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Air Conditioning</span>
                <span className="font-medium">
                  {propertyData.airConditioningType || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Heating Type</span>
                <span className="font-medium">
                  {propertyData.heatingType || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Heating Fuel</span>
                <span className="font-medium">
                  {propertyData.heatingFuelType || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Fireplace</span>
                <span className="font-medium">
                  {propertyData.fireplace ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Plumbing Fixtures</span>
                <span className="font-medium">
                  {propertyData.plumbingFixturesCount || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible> */}

      {/* Amenities & Features */}
      {/* <Collapsible
        open={openSections.amenities}
        onOpenChange={() =>
          setOpenSections((prev) => ({ ...prev, amenities: !prev.amenities }))
        }
        className="border rounded-lg border-tax-borderGray"
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left">
          <div className="flex items-center">
            <Info className="h-5 w-5 mr-2 text-tax-brightBlue" />
            <h3 className="text-lg font-semibold text-tax-deepBlue">
              Amenities & Features
            </h3>
          </div>
          {openSections.amenities ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              <FeatureTag name="HOA" value={propertyData.hoa} />
              <FeatureTag name="Pool" value={propertyData.pool} />
              <FeatureTag name="Deck" value={propertyData.deck} />
              <FeatureTag name="Patio" value={propertyData.patio} />
              <FeatureTag name="RV Parking" value={propertyData.rvParking} />
              <FeatureTag name="Attic" value={propertyData.attic} />
              <FeatureTag name="Breezeway" value={propertyData.breezeway} />
              <FeatureTag name="Carport" value={propertyData.carport} />
              <FeatureTag
                name="Fire Sprinklers"
                value={propertyData.safetyFireSprinklers}
              />
              {propertyData.porchType && propertyData.porchArea && (
                <div className="flex items-center px-2 py-1.5 bg-gray-100 rounded-md">
                  <span className="text-xs">
                    {propertyData.porchType}:{' '}
                    <span className="font-bold">
                      {formatArea(propertyData.porchArea)}
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible> */}

      {/* Legal Information */}
      <Collapsible
        open={openSections.legal}
        onOpenChange={() =>
          setOpenSections((prev) => ({ ...prev, legal: !prev.legal }))
        }
        className="border rounded-lg border-tax-borderGray"
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left">
          <div className="flex items-center">
            <FileText className="h-5 w-5 mr-2 text-tax-brightBlue" />
            <h3 className="text-lg font-semibold text-tax-deepBlue">
              Legal Information
            </h3>
          </div>
          {openSections.legal ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              <div className="flex justify-between">
                <span className="text-tax-neutralGray text-left">
                  Legal Description
                </span>
                <span className="font-medium">
                  {propertyData.legalDescription || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Lot Number</span>
                <span className="font-medium">
                  {propertyData.lotNumber || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Property Use</span>
                <span className="font-medium">
                  {propertyData.propertyUse || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Property Use Code</span>
                <span className="font-medium">
                  {propertyData.propertyUseCode || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Units Count</span>
                <span className="font-medium">
                  {propertyData.unitsCount || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Buildings Count</span>
                <span className="font-medium">
                  {propertyData.buildingsCount || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">
                  Homeowner Tax Exemption
                </span>
                <span className="font-medium">
                  {propertyData.taxExemptionHomeownerFlag ? 'Yes' : 'No'}
                </span>
              </div>
              {propertyData.taxDelinquentYear && (
                <div className="flex justify-between">
                  <span className="text-tax-neutralGray">
                    Tax Delinquent Year
                  </span>
                  <span className="font-medium">
                    {propertyData.taxDelinquentYear}
                  </span>
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Geographic & Environmental */}
      <Collapsible
        open={openSections.geo}
        onOpenChange={() =>
          setOpenSections((prev) => ({ ...prev, geo: !prev.geo }))
        }
        className="border rounded-lg border-tax-borderGray"
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left">
          <div className="flex items-center">
            <Info className="h-5 w-5 mr-2 text-tax-brightBlue" />
            <h3 className="text-lg font-semibold text-tax-deepBlue">
              Geographic & Environmental
            </h3>
          </div>
          {openSections.geo ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Latitude</span>
                <span className="font-medium">
                  {propertyData.latitude
                    ? propertyData.latitude.toFixed(6)
                    : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Longitude</span>
                <span className="font-medium">
                  {propertyData.longitude
                    ? propertyData.longitude.toFixed(6)
                    : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Census Block</span>
                <span className="font-medium">
                  {propertyData.censusBlock || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Census Block Group</span>
                <span className="font-medium">
                  {propertyData.censusBlockGroup || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Census Tract</span>
                <span className="font-medium">
                  {propertyData.censusTract || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Flood Zone</span>
                <span className="font-medium">
                  {propertyData.floodZoneDescription || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Sales & Value History */}
      {/* <Collapsible
        open={openSections.sales}
        onOpenChange={() =>
          setOpenSections((prev) => ({ ...prev, sales: !prev.sales }))
        }
        className="border rounded-lg border-tax-borderGray"
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left">
          <div className="flex items-center">
            <CalendarIcon className="h-5 w-5 mr-2 text-tax-brightBlue" />
            <h3 className="text-lg font-semibold text-tax-deepBlue">
              Sales & Value History
            </h3>
          </div>
          {openSections.sales ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
              <div className="flex justify-between">
                <span className="text-tax-neutralGray">Last Sale Date</span>
                <span className="font-medium">
                  {propertyData.lastSaleDate
                    ? new Date(propertyData.lastSaleDate).toLocaleDateString()
                    : 'N/A'}
                </span>
              </div>
              {propertyData.pricePerSquareFoot > 0 && (
                <div className="flex justify-between">
                  <span className="text-tax-neutralGray">Price Per Sq Ft</span>
                  <span className="font-medium">
                    ${propertyData.pricePerSquareFoot.toFixed(2)}
                  </span>
                </div>
              )}
              {propertyData.lastSalePrice && (
                <div className="flex justify-between">
                  <span className="text-tax-neutralGray">Sale Price</span>
                  <span className="font-medium">
                    {formatCurrency(propertyData.lastSalePrice)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible> */}
    </div>
  );
};

// Feature tag component for amenities
const FeatureTag: React.FC<{
  name: string;
  value: boolean;
}> = ({ name, value }) => {
  if (value === undefined || value === null) return null;
  return (
    <div
      className={cn(
        'flex items-center px-3 py-1.5 rounded-md',
        value ? 'bg-green-100' : 'bg-gray-100',
      )}
    >
      <span className="mr-1">{value ? '✅' : '❌'}</span>
      <span className="text-sm">{name}</span>
    </div>
  );
};

export default PropertyDisplay;
